/* Complimentary store service; no third-party library or checkout script injection. */
(function (host, build) {
  'use strict';
  const library = build();
  if (typeof module === 'object' && module.exports) module.exports = library;
  if (!host.document) return;
  const node = host.document.getElementById('in-protection-config');
  if (!node || host.IdolnestProtection) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch { return; }
  const request = async (path, body) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(config.root.replace(/\/$/, '') + '/' + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await response.json();
      if (!response.ok) throw new Error(config.error);
      return data;
    } catch { throw new Error(config.error); }
    finally { clearTimeout(timer); }
  };
  const service = library.createService(config, request);
  const render = cart => {
    if (!service.isIncluded(cart)) return '';
    return document.getElementById('InProtectionCardTemplate')?.innerHTML || '';
  };
  const renderSlots = cart => {
    document.querySelectorAll('[data-in-protection-slot]').forEach(slot => { slot.innerHTML = render(cart); });
    const count = document.querySelector('[data-in-cart-page-count]');
    if (count) count.textContent = service.itemCount(cart);
  };
  const ensure = async cart => { const current = await service.ensure(cart); renderSlots(current); return current; };
  const cartBody = form => {
    const body = new FormData(form);
    for (const key of [...body.keys()]) if (key.startsWith('shopify-additional-items[')) body.delete(key);
    return body;
  };
  const updateProductForm = (form, variant) => {
    form?.querySelectorAll('[data-in-protection-additional] input').forEach(input => {
      input.disabled = !config.enabled || !config.safe || variant?.requiresShipping !== true;
    });
  };
  host.IdolnestProtection = {...service, ensure, render, cartBody, updateProductForm};
  document.addEventListener('shopify:section:load', event => {
    const next = event.target.querySelector('#in-protection-config');
    if (!next) return;
    try { Object.assign(config, JSON.parse(next.textContent)); } catch { return; }
    document.querySelectorAll('form.in-product-form').forEach(form => {
      const data = form.closest('[data-in-product]')?.querySelector('[data-in-product-json]');
      try { const variants = JSON.parse(data.textContent).variants; updateProductForm(form, variants.find(variant => String(variant.id) === String(form.elements.id?.value))); } catch { /* No accelerated checkout fields in this form. */ }
    });
    document.dispatchEvent(new CustomEvent('idolnest:protection:changed'));
  });
  let checkoutBusy = false;
  document.addEventListener('submit', async event => {
    const form = event.target, submitter = event.submitter;
    if (!(form instanceof HTMLFormElement)) return;
    let action;
    try { action = new URL(form.action, location.href); } catch { return; }
    if (action.origin !== location.origin || action.pathname.replace(/\/$/, '') !== config.cart.replace(/\/$/, '')) return;
    const checkout = submitter?.name === 'checkout';
    const update = form.id === 'InCartPageForm' && submitter?.name === 'update';
    if (!checkout && !update) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy || !form.reportValidity()) return;
    checkoutBusy = true;
    form.setAttribute('aria-busy', 'true');
    const buttons = [...form.querySelectorAll('button[type="submit"]')].map(button => [button, button.disabled]);
    buttons.forEach(([button]) => { button.disabled = true; });
    let errorNode = form.querySelector('[data-in-protection-error]');
    if (errorNode) errorNode.remove();
    try {
      const inputs = [...form.querySelectorAll('[data-in-cart-update-key]')];
      let cart;
      if (inputs.length) {
        const updates = Object.fromEntries(inputs.map(input => [input.dataset.inCartUpdateKey, Math.max(0, Math.trunc(Number(input.value) || 0))]));
        cart = await request('cart/update.js', {updates});
      }
      await ensure(cart);
      if (checkout) location.assign(config.root.replace(/\/$/, '') + '/checkout');
      else location.reload();
    } catch (error) {
      errorNode = document.createElement('p');
      errorNode.className = 'in-protection-error';
      errorNode.dataset.inProtectionError = '';
      errorNode.setAttribute('role', 'alert');
      errorNode.tabIndex = -1;
      errorNode.textContent = error.message || config.error;
      (form.querySelector('aside') || form).append(errorNode);
      errorNode.focus();
    } finally {
      checkoutBusy = false;
      form.removeAttribute('aria-busy');
      buttons.forEach(([button, disabled]) => { button.disabled = disabled; });
    }
  }, true);
  host.addEventListener('pageshow', event => { if (event.persisted) ensure().catch(() => {}); });
})(typeof window === 'undefined' ? {} : window, function () {
  'use strict';
  function createService(config, request) {
    const variantId = String(config.variantId || '');
    const isProtection = item => variantId !== '' && String(item.variant_id ?? item.id) === variantId;
    const merchandise = cart => (cart?.items || []).filter(item => !isProtection(item));
    const itemCount = cart => merchandise(cart).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const eligible = cart => merchandise(cart).some(item => item.requires_shipping === true);
    const lines = cart => (cart?.items || []).filter(isProtection);
    const isFree = item => item.requires_shipping === false && Number(item.price) === 0 && Number(item.final_line_price) === 0;
    const isIncluded = cart => config.enabled && eligible(cart) && lines(cart).length === 1 && lines(cart)[0].quantity === 1 && isFree(lines(cart)[0]);
    const failure = () => new Error(config.error || 'Unable to prepare complimentary protection. Please retry.');
    const remove = async items => request('cart/update.js', {updates: Object.fromEntries(items.map(item => [item.key, 0]))});
    async function synchronize(current) {
      let cart = current || await request('cart.js');
      if (!Array.isArray(cart?.items)) throw failure();
      let protection = lines(cart);
      // Never retain an accidentally repriced or physical protection item.
      if (protection.some(item => !isFree(item))) { await remove(protection); throw failure(); }
      if (!config.enabled || !eligible(cart)) return protection.length ? remove(protection) : cart;
      if (!variantId || !config.safe) throw failure();
      if (!protection.length) {
        const product = await request('products/' + encodeURIComponent(config.handle) + '.js');
        const variant = product.variants?.find(item => String(item.id) === variantId);
        if (!variant?.available || Number(variant.price) !== 0 || variant.requires_shipping !== false) throw failure();
        await request('cart/add.js', {items: [{id: variantId, quantity: 1, properties: config.properties}]});
        cart = await request('cart.js');
        protection = lines(cart);
      }
      if (protection.some(item => !isFree(item))) { await remove(protection); throw failure(); }
      if (protection.length !== 1 || protection[0]?.quantity !== 1) {
        if (!protection.length) throw failure();
        cart = await request('cart/update.js', {updates: Object.fromEntries(protection.map((item, index) => [item.key, index === 0 ? 1 : 0]))});
      }
      // Recheck after writes: the customer may have changed the cart in another tab.
      if (!eligible(cart)) return lines(cart).length ? remove(lines(cart)) : cart;
      if (!isIncluded(cart)) throw failure();
      return cart;
    }
    let tail = Promise.resolve(), pending = 0;
    function ensure(cart) {
      const useProvided = pending === 0;
      pending++;
      const work = tail.catch(() => {}).then(() => synchronize(useProvided ? cart : undefined));
      tail = work;
      return work.then(value => { pending--; return value; }, error => { pending--; throw error; });
    }
    return {ensure, isProtection, merchandise, itemCount, eligible, isIncluded};
  }
  return {createService};
});
